import { Message } from './message';
import { Inject, Injectable } from '@angular/core';
import { IResourceContext } from 'shell-models-lib';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

/**
 * The messaging service facilitates sending messages between any component in the system using channels. Components
 * can subscribe to a topic and will get notified whenever a message is broadcasted on that channel.
 */
@Injectable({
    providedIn: 'root'
})
export class MessagingService {
    constructor(@Inject(RESOURCE_CONTEXT_DATA) public contextData: IResourceContext) { }
    /**
     * Indexes channels and their associated subscribers.
     */
    private channelSubscribers: Map<MessagingServiceChannel, Set<IMessageSubscriber>> = new Map<
        MessagingServiceChannel,
        Set<IMessageSubscriber>
    >();

    /**
     * Broadcasts a new message to a channel.
     * @param channel The channel to broadcast to.
     * @param message The message to broadcast.
     * @param broadcaster An optional broadcaster.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public broadcast<T>(channel: MessagingServiceChannel, message: Message<T>, broadcaster?: any): void {
        if (!message) {
            throw Error('message must be set');
        }

        if (this.channelSubscribers.has(channel)) {
            const channelSubscriptions = this.channelSubscribers.get(channel);
            if (channelSubscriptions) {
                channelSubscriptions.forEach((subscriber) => {
                    // fire and forget, don't handle promise errors, don't block on each subscriber
                    subscriber.onNewMessage(channel, message, broadcaster);
                });
            }
        }
    }

    /**
     * Subscribes to messages in a channel.
     * @param channel The channel to subscribe to.
     * @param subscriber A subscriber to notify of new messages in the channel.
     */
    public subscribe(channel: MessagingServiceChannel, subscriber: IMessageSubscriber): void {
        if (!subscriber) {
            throw Error('subscriber must be set');
        }

        if (!this.channelSubscribers.has(channel)) {
            // this is the channel's first subscriber
            this.channelSubscribers.set(channel, new Set<IMessageSubscriber>());
        }

        // sets don't store dupes so no need to check if the subscriber exists
        const channelSubscriptions = this.channelSubscribers.get(channel);
        if (channelSubscriptions) {
            channelSubscriptions.add(subscriber);
        }
    }

    /**
     * Stops listening to messages from a channel.
     * @param channel The channel to unsubscribe from.
     * @param subscriber The subscriber to delist.
     */
    public unsubscribe(channel: MessagingServiceChannel, subscriber: IMessageSubscriber): void {
        if (!subscriber) {
            throw Error('subscriber must be set');
        }

        if (this.channelSubscribers.has(channel)) {
            const channelSubscriptions = this.channelSubscribers.get(channel);
            if (channelSubscriptions) {
                channelSubscriptions.delete(subscriber);
            }
        }
    }
}

/**
 * Enumerates messaging channels in the system.
 */
export enum MessagingServiceChannel {
    /**
     * Used to inform components that the UX pertaining to an error need to be highlighted
     * for users to know where to fix the error.
     */
    ErrorUiNavigation,

    /**
     * Regular UI navigation message.
     */
    UiNavigation,

    /**
     * Used to navigate to specific transformation.
     */
    TransformationUiNavigation,

    /**
     * Fired when a new property tab is selected.
     */
    PropertyTabSelectionChange,

    /**
     * Fired when the value of a parameterized property is changed.
     */
    ParameterizedValueUpdates,

    /**
     * Fired when a new parameter is added
     */
    PipelineParametersUpdated,

    /**
     * Fired when an execute pipeline activity selected pipeline is changed
     */
    ExecutePipelineActivityPipelineChanged,

    /**
     * Fired when the plus icon is clicked on switch activity to add a case (only available for 5x5)
     */
    PipelineSwitchActivityAddCase,

    /**
     * Fired on mouse click of a nested activity on a container activity (only available for 5x5)
     */
    PipelineNestedActivitySelected,

    /**
     * Fired when a container activity is selected (only available for 5x5)
     */
    PipelineContainerActivitySelected,

    /**
     * Fired when the dataset of an activity is updated
     */
    DatasetUpdates,

    /**
     * Fired when an entity's dynamic property is updated. For instance, when file format is switched to a different
     * type causing new sub fields to be added or removed.
     */
    EntityDynamicPropertiesUpdated,

    /**
     * Data factory could not be loaded due to errors in it.
     */
    DataFactoryLoadFailures,

    /**
     * Fired when a new power query sink is selected
     */
    PowerQuerySelected,

    /**
     * Fired when a new power query sink is selected
     */
    PowerQuerySinkSelected,

    /**
     * Fired when mashup script is updated
     */
    PowerQueryScriptUpdates,

    /**
     * Used to inform tab container to switch to nested pipeline canvas.
     */
    NestedPipelineNavigation,

    /**
     * Used to inform tab container to return to original pipeline and focus on parent activity
     */
    NestedPipelineReturnNavigation,

    /**
     * Used to inform authoring pipeline canvas to refresh canvas nodes
     */
    RefreshCanvas,

    /**
     * Used to inform row relative url setting for call transform has changed
     */
    RowRelativeUrlChanged,

    /**
     * Used to inform authoring power query canvas to validate parameters
     */
    ValidatePowerQueryParameters,

    /**
     * Used to inform power query parameters panel to pop up
     */
    OpenPowerQueryParametersPanel,

    /**
     * Used to inform authoring power query canvas to refresh mashup editor
     */
    InitWranglingCanvas,

    /**
     * Used to inform authoring power query canvas to refresh mashup editor
     */
    WranglingCanvasHasLoaded,

    /**
     * Used to inform all authoring power query canvas that navigation to authoring was made
     */
    AuthoringNavigation,

    /**
     * Used to inform power query canvas that tab navigation was made
     */
    PowerQueryTabNavigation,

    /**
     * Used to inform authoring power query settings tab to request focus
     */
    OpenPowerQuerySettings,

    /**
     * Used to inform authoring pipeline canvas to select canvas nodes
     */
    SelectCanvasEntity,

    /**
     * Used to inform authoring pipeline navigation controller to refresh navigation path
     */
    RefreshNavigationPath,

    /**
     * Used to inform all filter pills to remove focus
     */
    FocusedFilterPill,

    /**
     * Used to inform any errors we found for git repo.
     */
    GitRepoError,

    /*
     * Used to inform if Git Repository is setup.
     */
    GitRepoSetup,

    /*
     * Used to inform if Git Repository setup is cancelled.
     */
    GitRepoSetupCancelled,

    /*
     * Used to inform if Git Repository is removed.
     */
    GitRepoRemoved,

    /*
     * Used to inform GitHub token is provided.
     */
    GitTokenProvided,

    /*
     * Used to inform publish branch is changed.
     */
    GitPublishBranchChanged,

    /*
     * Used to inform git auto save is enabled.
     */
    GitAutoSaveEnabled,

    /*
     * Used to inform an additional account was used to connect to VSTS
     */
    VstsCrossTenantAccountSignIn,

    /**
     * Used to communicate global model refreshes.
     */
    ModelsRefreshed,

    /**
     * Used to inform dataflow canvas to update canvas.
     */
    DataFlowUpdateCanvas,

    /**
     * Used to inform dataflow preview to refresh.
     */
    DataFlowRefreshPreview,

    /**
     * Used to inform dataflow canvas to show script.
     */
    DataFlowShowScript,

    /**
     * Used to inform dataflow canvas to show JSON code.
     */
    DataFlowShowJson,

    /**
     * Used to inform dataflow canvas to show debug settings.
     */
    DataFlowShowDebugSettings,

    /**
     * Fired when the value of a dataflow expression is updated
     */
    DataFlowExpressionUpdate,

    /**
     * Fired when the value of columns are added or removed.
     */
    DataFlowColumnCountUpdate,

    /**
     * Fired when the description can be updated for the transformations.
     */
    DataFlowTransformationDescriptionUpdate,

    /**
     * Fired when a data flow sink ordering is updated
     */
    DataFlowSinkOrderUpdate,

    /**
     * Fired when a source or sink is updated.
     */
    DataFlowTransformationWithDatasetUpdate,

    /**
     * Used to inform dataflow canvas to update canvas.
     */
    DataFlowUpdateStreams,

    /**
     * Used to navigate to a flowlet
     */
    DataflowFlowletNavigation,

    /**
     * Used to inform dataflow that the inline linked service changed.
     */
    DataFlowInlineLinkedServiceChanged,

    /**
     * Fired whenever there is a need to revalidate.
     */
    RevalidationRequested,

    /**
     * Fired whenever there is a change to an output stream (add/remove/rename)
     */
    DataFlowOutputStreamUpdate,

    /**
     * Fired whenever there is a change to a flowlet in a Execute Flowlet activity
     */
    DataFlowFlowletUpdate,

    /**
     * Fired when a linked service is updated
     */
    LinkedServiceUpdates,

    /**
     * Fired when a dataset changes its referenced linked service
     */
    DatasetLinkedServiceChange,

    /**
     * Fired when a format dataset changes its compression type, this affects compression setting type in copy source model
     */
    FormatDatasetCompressionChange,

    /**
     * Fired whenever pipeline variable is modified
     */
    PipelineVariablesAdded,
    PipelineVariablesRemoved,
    PipelineVariablesChanged,

    /**
     * Fired to notify pipeline output component about the status of the dataflow debug session
     */
    DebugClusterProvisioningStatus,

    /**
     * Fired when a tab is closed manually
     */
    TabClosed,

    /**
     * Fired ssis activity global status
     */
    ExecuteSSISPackageActivityMetadataRefreshStart,
    ExecuteSSISPackageActivityMetadataRefreshEnd,

    /**
     * Fired when a property changed results in retry preview data
     */
    ValueChangeRetryPreview,

    /**
     * Fired when we require any related components to refresh the its view
     */
    ComponentRefreshEvent,

    /**
     * Fired when IR changed in linkedservice creation/edit. (internal use within linkedservice authoring)
     */
    IRUpdatedEvent,

    /**
     * Fired an Event for PE Creation
     */
    PECreatedEvent,

    /**
     * Fired when the IR in linkedservice/dataset changed. (work for all madrid component)
     */
    IRUpdatedInPayloadEvent,

    /**
     * when copy activity column mapping has change, it will be fired. Message entity is copy activity itself
     */
    CopyColumnMappingUpdate,

    /**
     * Fired when a column mapping is changed (i.e. mapping is removed, renamed, etc.)
     */
    DataFlowColumnMappingUpdate,

    /**
     * Fired when a component wants to switch panel tabs within its own component
     */
    PanelTabUINavigation,

    /**
     * Fired when a Data Flow requires a new debug job version
     */
    DataFlowNewDebugJobRequired,

    /**
     * Fired when a Data Flow parameter is added, removed, or changed
     */
    DataFlowParameterChange,

    /**
     * Fired when small screen layout
     */
    SmallScreenToggle,

    /**
     * Fired when FactoryResources clicked from small screen NavMenu
     */
    SmallScreenFactoryResourcesNavigation,

    /**
     * Fired when properties sidebar button is clicked in the factory options topbar
     */
    TogglePropertiesSidebar,

    /**
     * Fired when canvas details panel is clicked when minimized
     */
    ExpandCanvasDetailsPanel,

    /**
     * Fired when a Data Flow option is changed.
     */
    DataFlowCdcChange,

    /**
     * Fired when concept tree from resource list is changed
     */
    ConceptTreeChanged,

    /**
     * Fired when concept tree target from resource list is changed
     */
    ConceptTreeTargetChanged,

    /**
     * Fired when folder tree from resource list is changed
     */
    FolderTreeTargetChanged,

    /**
     * Fired when store read settings are changed
     */
    StoreReadSettingsChanged,

    /**
     * Fired when store read settings are changed
     */
    CopySourceChanged,

    /**
     * Fired when copy sink activities are changed
     */
    CopySinkChanged,

    /**
     * babylon permission result
     */
    BabylonPermissionResult,

    /**
     * name changed from properties tab
     */
    WranglingDataflowRenamed,

    /**
     * Fires when power query is valid
     */
    WranglingDataflowValid,

    /**
     * Fires when power query is invalid
     */
    WranglingDataflowInvalid,

    /**
     * Fires when dataflow table changed
     */
    DataFlowTableChanged,

    /**
     * Show teaching bubbles for autosave
     */
    AutosaveTeachingBubbles,

    /**
     * Open the portal settings tab
     */
    OpenPortalSettings,

    /**
     * Change Canvas + Preview Style
     */
    DataFlowCanvasStyleChanged,

    /**
     * Fired when dataflow import schema
     */
    DataFlowSchemaImported,

    /**
     * Fired when a property bag bound in a dynamic control or its properties changed
     */
    DynamicPropertyBagChanged,

    /**
     * Fired when the recent resources have been updated
     */
    RecentResourcesChanged,

    /**
     * Fired when the global search history has been updated
     */
    SearchHistoryChanged,

    /**
     * Fired when a dynamic controls' dependence changed
     */
    DynamicControlsDependenceChanged,

    /**
     * Fired when triggers a Monaco command
     */
    MonacoCommandFired,

    /**
     * Fired when a publish event is successful
     */
    PublishSucceeded,

    /**
     * Fired when an activity's state property has changed
     */
    ActivityStateChanged,

    /**
     * Fired when an activity's inactivated marked as property has changed
     */
    InactivatedMarkAsChanged,

    /**
     * Fired when an activity's state property has changed from the canvas
     */
    ActivityStateChangedFromCanvas,
    
    /**
     * Fired when an activity's state property has changed from the canvas
     */
    ActivityStateChangedFromValidation,

    /**
     * Fired when a credential is updated
     */
    CredentialUpdates,

    /**
     * Fired when file format is changed
     */
    FileFormatChange = 'FileFormatChange',

    /**
     * Fired when requesting the run details sidebar to be open.
     */
    OpenRunDetailsSidebar = 'OpenRunDetailsSidebar',

    /**
     * Fired when run details sidebar is closed.
     */
    OnCloseRunDetailsSidebar = 'OnCloseRunDetailsSidebar',

    /**
     * Fired when we want to close the dynamic content flyout.
     */
    DynamicContentFlyoutClose = 'DynamicContentFlyoutClose',

    /**
     * Fired when requesting the copy job run details sidebar to be open.
     */
    OpenCopyJobRunDetails = 'OpenCopyJobRunDetails',
    
    /**
     * Fired when a template tour has been selected from gallery
     */
    TemplateTourSelected = 'TemplateTourSelected',

    /**
     * Fired when copy mapping is changed.
     */
    CopyMappingUpdate = 'CopyMappingUpdate'
}

/**
 * A message subscriber contract.
 */
export interface IMessageSubscriber {
    /**
     * Invoked when a new message from a channel is broadcasted.
     * @param channel The message channel.
     * @param message The message broadcasted.
     * @param broadcaster The message broadcaster.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onNewMessage(channel: MessagingServiceChannel, message: Message<{}>, broadcaster: any): Promise<void>;
}
